/**
 * Copyright (c) 2018, salesforce.com, inc.
 * All rights reserved.
 * Licensed under the BSD 3-Clause license.
 * For full license text, see LICENSE.txt file in the repo root or
 * https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react';
import PropTypes from 'prop-types';
import { Switch, Route } from 'react-router-dom';
import MediaQuery from 'react-responsive';
import _ from 'lodash';
import styled from 'styled-components';
import styles from '../../styles';

import Localized from '../../../shared/components/Localized';
import Tabs from '../../components/Tabs';
import LandingPageIncidentList from '../LandingPageIncidentList';
import LandingPageMaintenanceList from '../LandingPageMaintenanceList';
import RecentInstanceList from '../RecentInstanceList';

// V2 components
import ProductSideNav from '../../../../components/ProductSideNav';

const Wrapper = styled.div`
  @media (min-width: 1024px) {
    padding-left: 15rem;
  }
`;
const ProductsBody = styled.div`
  position: relative;
  width: 100%;
  min-height: 12rem;
  display: flex;

  @media (max-width: 767px) {
    margin-top: -5px;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    margin-top: -4px;
  }
`;
const UpperBody = styled.div`
  position: relative;
  width: 100%;
`;
const SearchPane = styled.div`
  height: 100%;
  height: 256px;
  background-color: white;
  margin-bottom: 1rem;
  ${styles.borders.card}

  margin: 0.75rem 0.75rem 0 0.75rem;
  padding-bottom: 1rem;
  @media (max-width: 767px) {
    margin: 0.75rem 0 0 0;
  }

  @media (max-width: 767px) {
    border-radius: 0;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    height: 226px;
  }
`;

const Pane = styled.div`
  height: 100%;
  width: 714px;
  min-height: 381px;
  background-color: white;
  margin-bottom: 1rem;
  ${styles.borders.card}
  flex: 1 1 100%;
  margin: 0.75rem 0rem 0.75rem 0.75rem;

  padding-bottom: 1rem;
  @media (max-width: 767px) {
    margin: 0.75rem 0 0 0;
  }

  @media (max-width: 767px) {
    border-radius: 0;
  }
`;
const SideColumn = styled.div`
  flex: 0 0 auto;
  width: 306px;
  height: 100%;
  margin: 0.75rem;
  ${styles.borders.card}
`;

const TabsRow = styled.div`
  height: 4rem;
  background-color: white;

  @media (min-width: 1024px) {
    height: 3rem;
  }
`;

const ImageSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: row;
  background-color: white;
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
  height: 136px;
  background-image: url('/images/Bg_Cloud_L.png');
  background-size: cover;
  background-repeat: no-repeat;
  @media (min-width: 768px) and (max-width: 1024px) {
    background-image: url('/images/Bg_Cloud_M.png');
  }
  @media (min-width: 320px) and (max-width: 480px) {
    background-image: url('/images/Bg_Cloud_S.png');
    height: 114px;
  }
`;

const SearchSection = styled.div`
  width: 100%;
  height: 120px;
  display: flex;
  @media (min-width: 320px) and (max-width: 480px) {
    height: 94px;
  }
`;
const FormElement = styled.div.attrs({
  className: 'slds-form-element',
})`
  width: 100%;
`;
const FormElementControls = styled.div.attrs({
  className: 'slds-form-element__control fa',
})`
  display: flex;
  margin-top: 36px;
  > input + span {
    height: 48px;
    background-color: #00a1e0;
    width: 48px;
    border-radius: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    border-top-right-radius: 0.25rem;
    border-bottom-right-radius: 0.25rem;
  }
`;

const SearchInput = styled.input.attrs({
  className: 'slds-input',
  label: 'Search Instance, Domain, Pod, or MID',
})`
  width: 544px;
  height: 48px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  margin-left: 36px;
  flex: 0 1 auto;
  border-right: 0;
  &::-webkit-input-placeholder {
    font-size: 16px;
    font-weight: normal;
    font-style: normal;
    font-stretch: normal;
    line-height: 1.25;
    letter-spacing: normal;
    font-face: 'Salesforce Sans';
  }
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-left: 36px;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    margin-left: 20px;
    width: 386px;
    height: 54px;
  }
`;

const IconSpan = styled.span.attrs({
  className: 'slds-icon_container slds-icon-standard-account',
})`
  height: 48px;
  ${SearchInput}:focus + & {
    border-top: solid 0.5px #d8dde6;
    border-bottom: solid 0.5px #d8dde6;
    border-right: solid 0.5px #d8dde6;
    border-color: rgb(21, 137, 238); /* This is the blue color of the border */

    box-shadow: 0 0 3px #0070d2;
  }

  @media (min-width: 320px) and (max-width: 480px) {
    min-height: 54px;
    min-width: 54px;
    margin-right: 20px;
  }
`;
const ImageHeadingWrapper = styled.div`
  height:100%;
  width:100%
  display: flex;
  flex-direction:column;
`;
const ImageHeading = styled.p`
  margin-top: 38px;
  margin-left: 34px;
  flex: 0 1 auto;
  font-size: 28px;
  font-weight: 300;
  font-style: normal;
  font-stretch: normal;
  color: #00386d;
  line-height: 1.25;
  letter-spacing: normal;
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-left: 36px;
    margin-top: 36px;
    font-size: 24px;
  }
  @media (min-width: 320px) and (max-width: 480px) {
    margin-left: 20px;
    margin-top: 45px;
    margin-bottom: 44px;
    font-size: 20px;
  }
`;

const ImageSubHeader = styled.p`
  margin-left: 36px;
  margin-top: 8px;
  font-family: 'Salesforce Sans';
  font-size: 16px;
  font-weight: 300;
  font-style: normal;
  font-stretch: normal;
  line-height: normal;
  letter-spacing: normal;
  color: #16325c;
  @media (min-width: 768px) and (max-width: 1024px) {
    margin-top: 8px;
    margin-left: 36px;
  }
  @media (min-width: 320px) and (max-width: 634px) {
    display: none;
  }
`;
const AstroImage = styled.img.attrs({
  src: '/images/astro.svg',
  alt: '',
})`
  flex: 0 1 auto;
  margin-left: auto;
  margin-top: 14px;
  margin-right: 78px;
  @media (min-width: 320px) and (max-width: 480px) {
    margin-left: auto;
    margin-right: 34px;
  }
`;
const SearchSVG = styled.svg.attrs({
  className: 'slds-icon slds-icon--medium',
})``;
const HeadingWrapper = styled.div`
  padding: 1rem 1.25rem 1rem 1.25rem;
  background-color: ${styles.colors.linkWater};
  border-top-left-radius: 0.25rem;
  border-top-right-radius: 0.25rem;
  display: flex;

  @media (max-width: 767px) {
    padding: 1.25rem;
  }
`;
const LandingPageHeading = styled.div`
  font-size: 16px;
  color: #16325c;
`;
class LandingPage extends React.Component {
  static propTypes = {
    match: PropTypes.shape({
      url: PropTypes.string,
      path: PropTypes.string,
    }),
    requests: PropTypes.object,
  };

  constructor(props) {
    super(props);
    this.state = {
      menuOpen: false,
      inputActive: false,
      searchBoxValue: '',
      incidentCount: 0,
      maintenanceCount: 0,
    };
    this.toggleMenu = () => {
      return this.setState({ menuOpen: !this.state.menuOpen });
    };
    this.onEnter = this.onEnter.bind(this);
    this.onChange = this.onChange.bind(this);
    this.submitSearch = this.submitSearch.bind(this);
    this.setIncidentCount = this.setIncidentCount.bind(this);
    this.setMaintenanceCount = this.setMaintenanceCount.bind(this);
  }
  submitSearch(e) {
    const searchVal = this.state.searchBoxValue;
    if (searchVal.length) {
      this.setState({ searchBoxValue: '' });
      this.props.history.push('/search/' + searchVal);
    }
  }
  onChange(e) {
    this.setState({ searchBoxValue: e.target.value });
  }

  onEnter(e) {
    // stay on the page if user press enter and !value
    if (e.key === 'Enter' && e.target.value) {
      this.props.history.push('/search/' + e.target.value);
    }
  }
  setIncidentCount(newIncidentCount) {
    this.setState({ incidentCount: newIncidentCount });
  }
  setMaintenanceCount(newMaintenanceCount) {
    this.setState({ maintenanceCount: newMaintenanceCount });
  }

  render() {
    if (!this.props.requests.products.isCompleted) return null;

    const localizations = this.props.requests.localizations.value;
    const placeholderLocalizations = _.find(localizations, {
      modelName: 'landingPage',
      modelKey: 'searchPlaceholder',
      modelAttribute: 'label',
    });
    const placeholderText =
      typeof placeholderLocalizations === 'object' && placeholderLocalizations.hasOwnProperty('text')
        ? placeholderLocalizations.text
        : 'Search Instance, Domain, Pod, or MID';

    const tabs = [
      {
        name: <Localized modelKey="incidents" modelAttribute="label" modelName="tabs" />,
        url: this.props.match.url + '(/)?(incidents)?',
        to: `${this.props.match.url}/incidents`,
      },
      {
        name: <Localized modelKey="maintenance" modelAttribute="label" modelName="tabs" />,
        url: `${this.props.match.url}/maintenances`,
      },
    ];
    const mobileTabs = [
      ...tabs,
      {
        name: <Localized modelKey="Recent Instances" modelAttribute="label" modelName="tabs" />,
        url: `${this.props.match.url}/recentInstances`,
      },
    ];

    const products = (this.props.requests.products.value || [])
      .map(i => {
        return {
          key: i.key,
          url: `/products/${i.key}`,
          customIcon: true,
          order: i.order,
        };
      })
      .sort((a, b) => {
        return a.order - b.order;
      });

    products.unshift({
      key: 'Home',
      url: '/current',
      customIcon: false,
    });

    const currentProduct = this.props.requests.products.value.find(p => {
      return p.key === this.props.match.params.product;
    }) || { key: 'Home' };

    return (
      <Wrapper>
        <MediaQuery minWidth={1024}>
          <ProductSideNav />
        </MediaQuery>
        <UpperBody>
          <SearchPane>
            <ImageSection>
              <ImageHeadingWrapper>
                <ImageHeading>
                  <Localized modelKey="salesforceStatusWelcome" modelAttribute="label" modelName="heading" />
                </ImageHeading>
                <ImageSubHeader>
                  <Localized modelKey="salesforceStatusDescription" modelAttribute="label" modelName="description" />
                </ImageSubHeader>
              </ImageHeadingWrapper>
              <AstroImage></AstroImage>
            </ImageSection>
            <SearchSection>
              <FormElement>
                <FormElementControls>
                  <label htmlFor="text-input-id-1" className="slds-assistive-text">
                    {placeholderText}
                  </label>
                  <SearchInput
                    value={this.state.searchBoxValue}
                    type="text"
                    id="text-input-id-1"
                    name="text-input-id-1"
                    onChange={this.onChange}
                    onKeyPress={this.onEnter}
                    placeholder={placeholderText}
                  />
                  <IconSpan onClick={this.submitSearch}>
                    <SearchSVG aria-hidden="true">
                      <use xlinkHref="/assets/icons/standard-sprite/svg/symbols.svg#search"></use>
                    </SearchSVG>
                  </IconSpan>
                </FormElementControls>
              </FormElement>
            </SearchSection>
          </SearchPane>
        </UpperBody>
        <MediaQuery minWidth={769}>
          <ProductsBody>
            <Pane>
              <main id="maincontent">
                <HeadingWrapper>
                  <LandingPageHeading>
                    <Localized modelKey="currentStatus" modelAttribute="label" modelName="tabs" />
                  </LandingPageHeading>
                </HeadingWrapper>
                <TabsRow>
                  <Tabs items={tabs} />
                </TabsRow>
                <Switch>
                  <Route path={`${this.props.match.path}/maintenances`}>
                    <LandingPageMaintenanceList setMaintenanceCount={this.setMaintenanceCount} />
                  </Route>
                  <Route path={`${this.props.match.path}/(incidents)?`}>
                    <LandingPageIncidentList setIncidentCount={this.setIncidentCount} />
                  </Route>
                </Switch>
              </main>
            </Pane>
            <SideColumn>
              <HeadingWrapper>
                <LandingPageHeading>
                  <Localized modelKey="recentInstances" modelAttribute="label" modelName="heading" />
                </LandingPageHeading>
              </HeadingWrapper>
              <RecentInstanceList></RecentInstanceList>
            </SideColumn>
          </ProductsBody>
        </MediaQuery>
        <MediaQuery maxWidth={768}>
          <ProductsBody>
            <Pane>
              <MediaQuery maxWidth={768}>
                {matchs => {
                  return (
                    <TabsRow>
                      <Tabs items={matchs ? mobileTabs : tabs} />
                    </TabsRow>
                  );
                }}
              </MediaQuery>
              <Switch>
                <Route path={`${this.props.match.path}/maintenances`}>
                  <LandingPageMaintenanceList />
                </Route>
                <Route path={`${this.props.match.path}/(recentInstances)`}>
                  <RecentInstanceList />
                </Route>
                <Route path={`${this.props.match.path}/(incidents)?`}>
                  <LandingPageIncidentList />
                </Route>
              </Switch>
            </Pane>
          </ProductsBody>
        </MediaQuery>
      </Wrapper>
    );
  }
}

export default LandingPage;
